package dev.lingvo.shared

import kotlinx.serialization.Serializable

@Serializable
data class KeyDto(
    val id: String,
    val projectId: String,
    val fileId: String,
    val name: String,
    val createdAt: String,
    val updatedAt: String,
    val context: String? = null,
    val isPlural: Boolean = false,
    val maxLength: Int? = null,
    val tags: List<String> = emptyList(),
    val position: Int = 0,
)

@Serializable
data class TranslationDto(
    val id: String,
    val keyId: String,
    val locale: String,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    val origin: TranslationOrigin = TranslationOrigin.HUMAN,
    val mtProvider: MtProvider? = null,
    val state: TranslationState = TranslationState.EMPTY,
    /**
     * Where this sits in review, which is a different question from [state].
     *
     * A string can be TRANSLATED and waiting for a reviewer, TRANSLATED and sent back with a note,
     * or TRANSLATED and simply not submitted. One enum could not hold all three without breaking the
     * wire contract the CLI and every stored export depend on.
     */
    val reviewStatus: ReviewStatus = ReviewStatus.NONE,
    /** Why a reviewer sent it back. Present only while [reviewStatus] is CHANGES_REQUESTED. */
    val reviewNote: String? = null,
    val updatedBy: String? = null,
    val updatedAt: String,
    /**
     * The source changed after this translation was written.
     *
     * A badge, never a gate. Copy churns constantly on a mobile app, and a signal that blocks a
     * release on every typo fix is a signal people learn to route around — so this marks a row and
     * says what changed, and nothing else.
     */
    val stale: Boolean = false,
    /** Exact target CAS token. Null from a server that cannot enforce source-aware writes. */
    val revision: String? = null,
    /** Recorded source basis; null for legacy work whose basis cannot be established. */
    val sourceRevisionId: String? = null,
)

@Serializable
data class KeyRow(
    val key: KeyDto,
    val source: TranslationDto,
    val target: TranslationDto? = null,
    /**
     * Other languages, shown beside the one being edited.
     *
     * A Portuguese translator with good Spanish on screen is measurably faster, and the source
     * English is often the least useful reference available. **Capped at three, server-side**: each
     * one multiplies the row payload, and a key list is the heaviest response in the product.
     */
    val refTargets: Map<String, TranslationDto> = emptyMap(),
    /** Current canonical source, captured alongside the displayed source values. */
    val sourceSnapshot: SourceSnapshotDto? = null,
)

@Serializable
data class KeyListResponse(
    val total: Int = 0,
    val items: List<KeyRow> = emptyList(),
    /**
     * Pass this back as `after` to get the next page, or null when there is none.
     *
     * Opaque: its shape is ours to change. A client that parsed it would be relying on something
     * this response has never promised.
     *
     * Cursors rather than offsets because rows change state while someone pages through them:
     * approve ten strings under an "untranslated" filter and every later offset shifts by ten, so
     * ten rows are skipped and — because the client dedupes by id — never rendered at all. A
     * translator can work a filter to zero and leave ten strings untranslated believing the locale
     * is done.
     */
    val nextCursor: String? = null,
)

@Serializable
data class CreateKeyRequest(
    val fileId: String,
    val name: String,
    val context: String? = null,
    val isPlural: Boolean = false,
    val maxLength: Int? = null,
    val tags: List<String> = emptyList(),
    val sourceValue: String? = null,
    val sourcePluralForms: Map<PluralCategory, String>? = null,
)

@Serializable
data class UpdateKeyRequest(
    val name: String? = null,
    val context: String? = null,
    val maxLength: Int? = null,
    val tags: List<String>? = null,
    val isPlural: Boolean? = null,
)

/**
 * One locale's length budget for one key.
 *
 * Separate from [UpdateKeyRequest] because it is a different decision by a different person: the
 * key's own `maxLength` is a fact about a control, and this is a fact about what that control can
 * hold in one language. German needs about a fifth more room than English and Japanese about half,
 * so a single number cannot be right for both.
 */
@Serializable
data class SetKeyLocaleLimitRequest(val maxLength: Int)

@Serializable
data class UpsertTranslationRequest(
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    val state: TranslationState? = null,
)

@Serializable
data class BulkTranslationItem(
    val keyId: String,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    val state: TranslationState? = null,
)

@Serializable
data class BulkTranslationRequest(
    val locale: String,
    val items: List<BulkTranslationItem> = emptyList(),
)

@Serializable
data class BulkTranslationResponse(
    val updated: Int = 0,
)

@Serializable
data class HistoryEntryDto(
    val id: String,
    val keyId: String,
    val locale: String,
    val state: TranslationState,
    val authorId: String? = null,
    val createdAt: String,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    /**
     * Who or what produced this version.
     *
     * The column has existed since V2 and was populated all along — the memory prefill writes
     * `MEMORY` precisely so a bulk fill stays filterable — but the per-key query never selected it
     * and this record had nowhere to put it. A history that cannot say "a model wrote this one" is
     * a history that reads every row as a person's decision.
     */
    val origin: TranslationOrigin = TranslationOrigin.HUMAN,
    /**
     * Who wrote it, in words, as it stood at the time.
     *
     * [authorId] is `ON DELETE SET NULL`, so a closed account erases it — and null already means an
     * automated write, so a departed colleague's edits used to read as the system's. Frozen at write
     * time for the same reason the audit log freezes its actor. Null on rows written before V44, and
     * on genuinely automated writes.
     */
    val authorLabel: String? = null,
    val sourceSnapshot: SourceSnapshotDto? = null,
)

/**
 * One page of a key's history, newest first.
 *
 * A page rather than a list because history is unbounded: every save of every string for the life
 * of the project. [nextCursor] is opaque and is only present when there is genuinely more, so a
 * client can say "older" truthfully instead of showing a silently truncated list.
 */
@Serializable
data class KeyHistoryPageDto(
    val entries: List<HistoryEntryDto> = emptyList(),
    val nextCursor: String? = null,
)

@Serializable
data class CommentDto(
    val id: String,
    val keyId: String,
    val authorId: String? = null,
    val body: String,
    val createdAt: String,
    val locale: String? = null,
    val resolved: Boolean = false,
)

@Serializable
data class CreateCommentRequest(
    val body: String,
    val locale: String? = null,
)

/** Explicit discussion state; omission cannot silently resolve or reopen a comment. */
@Serializable
data class UpdateCommentRequest(val resolved: Boolean)

@Serializable
data class SuggestionDto(
    val value: String,
    val similarity: Double,
    val sourceKeyName: String,
    /**
     * Which project this came from.
     *
     * The memory is workspace-scoped, so a suggestion may come from a sibling project — and a
     * translator deciding whether to trust it needs to know that. Null for older responses.
     */
    val sourceProjectName: String? = null,
)

/**
 * Somebody else with this string open.
 *
 * **Editor-scoped and ephemeral.** It exists to stop two people typing over each other and appears
 * nowhere a workspace owner reads — a record of who had which string open when is a surveillance
 * feature nobody asked for, and this deliberately cannot become one: the rows expire in a minute and
 * are never written to history.
 */
@Serializable
data class PresenceDto(
    val userId: String,
    val name: String,
    val avatarColor: String,
)

/** Who else is on the keys the editor currently has on screen. */
@Serializable
data class PresenceResponse(
    /** Key id → the other people editing it. Keys with nobody else on them are absent. */
    val editing: Map<String, List<PresenceDto>> = emptyMap(),
)

/** A heartbeat from a focused field. */
@Serializable
data class PresenceHeartbeatRequest(
    val keyId: String,
    val locale: String,
    /** True when the field lost focus, so the row goes now rather than in a minute. */
    val released: Boolean = false,
    /** Which keys are on screen, so one request both reports and reads. */
    val visibleKeyIds: List<String> = emptyList(),
)

/**
 * Which locales a key has a translation in, as state rather than text.
 *
 * The keys list renders one 10px dot per locale per key; it needs the state and nothing else. Asking
 * for full translations to draw a dot is what made that page fetch the whole project once per
 * language ([KeyRow.refTargets] is capped at three for exactly that reason, and the coverage matrix
 * needs every visible locale, not three of them). Every locale requested for a key is present in
 * [states] — absent from a translation, not from this map: a locale with no translation reports
 * [TranslationState.EMPTY] rather than being left out, so "not translated" and "not computed" cannot
 * render identically.
 */
@Serializable
data class KeyCoverageDto(
    val keyId: String,
    val states: Map<String, TranslationState> = emptyMap(),
)

/**
 * One key in every language: the permalink.
 *
 * Its own shape rather than a one-row [KeyListResponse], because the questions differ. A list row
 * answers "what should I work on in this locale"; this answers "what is this string, everywhere" —
 * which is what somebody is asking when they paste the URL into a chat.
 */
@Serializable
data class KeyDetailDto(
    val key: KeyDto,
    val sourceLocale: String,
    /**
     * Every target locale the project has, **including ones with no translation row yet**.
     *
     * A language missing from this list reads as "not needed here", which is the opposite of what an
     * absent translation means.
     */
    val locales: List<String> = emptyList(),
    val translations: List<TranslationDto> = emptyList(),
    val comments: List<CommentDto> = emptyList(),
    /**
     * Where this string is used in the customer's own code, harvested by `lingvo push`.
     *
     * Empty for a project whose CLI has not scanned yet, which is the ordinary state and must read
     * as "we have not looked" rather than "it is unused" — a string with no recorded usage is a
     * string nobody has run a scan for, not a string nobody calls.
     */
    val usages: List<KeyUsageDto> = emptyList(),
)

/**
 * A key that was deleted and can be put back.
 *
 * [translations] is the number that would come back with it, and it is the field this exists for: a
 * reader deciding whether to restore is deciding whether somebody's work is on the other side of the
 * button, and "checkout.pay_now" alone does not tell them.
 */
@Serializable
data class DeletedKeyDto(
    val id: String,
    val name: String,
    val filePath: String,
    val deletedAt: String,
    val translations: Int,
)

@Serializable
data class DeletedKeysDto(val keys: List<DeletedKeyDto> = emptyList())

/**
 * How many keys one bulk request may touch.
 *
 * A thousand, refused above rather than truncated. Silently applying the first thousand of three
 * thousand is the failure this number exists to prevent: the receipt would say "1,000 keys tagged"
 * and be telling the truth about work that covered a third of what the reader selected, with
 * nothing saying which third.
 */
const val MAX_BULK_KEYS = 1000

/** What a bulk keys request does to every key it resolves. */
@Serializable
enum class BulkKeyOp {
    ADD_TAG,
    REMOVE_TAG,
    DELETE,
    RESTORE,
}

/**
 * "Every key matching what I am looking at", without listing them.
 *
 * The same five parameters `GET /projects/{slug}/keys` takes, and deliberately not a sixth: the
 * server resolves them through the identical predicate builder that endpoint uses, so the set a
 * page shows and the set a bulk request changes cannot disagree. A filter the list understands and
 * this does not would be a control that silently acts on a different set from the one on screen.
 *
 * Review status, staleness and the cursor are absent on purpose. The first two are not offered by
 * the surface this serves, and a cursor would make "all N matching" mean "the page you happen to be
 * on".
 */
@Serializable
data class BulkKeysFilter(
    val fileId: String? = null,
    val tag: String? = null,
    val search: String? = null,
    val state: TranslationState? = null,
    /**
     * Which language [state] is about.
     *
     * Absent means the project's keys regardless of any target language, exactly as an omitted
     * `locale` does on the list endpoint.
     */
    val locale: String? = null,
)

/**
 * One operation over many keys, in one transaction.
 *
 * Either [keyIds] — what the reader ticked — or [filter] — "all N matching", the second step the
 * selection bar offers once a page's worth is selected. Never both: two different answers to "which
 * keys" in one request is a request whose meaning depends on which one the server reads first.
 */
@Serializable
data class BulkKeysRequest(
    val op: BulkKeyOp,
    val keyIds: List<String> = emptyList(),
    val filter: BulkKeysFilter? = null,
    /** The tag to add or remove. Required by [BulkKeyOp.ADD_TAG] and [BulkKeyOp.REMOVE_TAG]. */
    val tag: String? = null,
)

/**
 * Why one key in the set was left alone.
 *
 * Every value is a race a reader can act on rather than an error they caused: somebody else deleted,
 * restored or renamed a key between the page loading and the button being pressed, or the tag was
 * already in the state the request asked for. The whole request still succeeds — refusing a hundred
 * keys because one of them moved is how a bulk control becomes one nobody presses.
 */
@Serializable
enum class BulkKeySkipReason {
    /** [BulkKeyOp.DELETE] on a key that was already deleted. */
    ALREADY_DELETED,

    /** A tag operation on a deleted key. Tags describe live keys; a deleted one is not one. */
    KEY_DELETED,

    /** [BulkKeyOp.RESTORE] on a key that is not deleted. */
    NOT_DELETED,

    /**
     * [BulkKeyOp.RESTORE] where a live key in the same file already holds the name.
     *
     * The single-key route answers this with a 409 naming the fix — rename or remove the key that
     * took the name. In a batch it is one row's outcome, not the batch's.
     */
    KEY_NAME_TAKEN,

    /** [BulkKeyOp.ADD_TAG] where the key already carries the tag. */
    TAG_ALREADY_PRESENT,

    /** [BulkKeyOp.REMOVE_TAG] where the key does not carry the tag. */
    TAG_NOT_PRESENT,
}

@Serializable
data class BulkKeySkipDto(
    val keyId: String,
    val reason: BulkKeySkipReason,
)

/**
 * What a bulk request actually did.
 *
 * [applied] counts rows the database changed, not rows the caller sent: a request that asks to tag
 * forty keys and finds thirty-eight already tagged reports two, so the receipt on screen cannot
 * claim work that never happened.
 */
@Serializable
data class BulkKeysResponse(
    val applied: Int = 0,
    val skipped: List<BulkKeySkipDto> = emptyList(),
)
